<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout">
      <router-view />
    </component>
  </div>
</template>

<script>
// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import useAppConfig from '@core/app-config/useAppConfig'

import { useCssVar } from '@vueuse/core'

import { loadLang } from '@core/libs/i18n/utils'
import { getUserData } from './auth/utils'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
  },
  sockets: {
    connect() {
      this.joinRoomOfService()
    },
    reconnect() {
      this.joinRoomOfService()
    },
    SERVICE_TRANSFERED(payload) {
      if (payload.oldCommunityStillExists) {
        if (this.$route.name !== 'select-service' && this.$route.name !== 'transfer-service') {
          return this.$router.push({ name: 'select-service' })
        }
      }

      if (this.$route.name !== 'select-community' && this.$route.name !== 'transfer-service') {
        return this.$router.push({ name: 'select-community' })
      }

      return null
    },
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    // provideToast({
    //   hideProgressBar: true,
    //   closeOnClick: false,
    //   closeButton: false,
    //   icon: false,
    //   timeout: 3000,
    //   transition: 'Vue-Toastification__fade',
    // })

    return {
      skinClasses,
    }
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
  },
  created() {
    // Set window width in store on page load
    this.$store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    // TODO: Check if we really need this, it's trowing an error
    // const { width: windowWidth } = useWindowSize()
    // watch(windowWidth, val => {
    //   store.commit('app/UPDATE_WINDOW_WIDTH', val)
    // })

    loadLang()

    // Load Paddle.js
    this.$loadScript('https://cdn.paddle.com/paddle/paddle.js').then(() => {
      // Paddle.js loaded
      if (process.env.NODE_ENV === 'development') {
        window.Paddle.Environment.set('sandbox')
      }

      window.Paddle.Setup({ vendor: parseInt(process.env.VUE_APP_PADDLE_VENDOR_ID, 10) })
    })
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2),
      )
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')

    const user = getUserData()
    if (user) {
      // eslint-disable-next-line no-undef
      Canny('identify', {
        appID: '64b7fc9503d517cd0f0e26f3',
        user: {
          // Replace these values with the current user's data
          id: user.id,
          email: user.email,
          name: user.username,

          // These fields are optional, but recommended:
          avatarURL: `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}`,
        },
      })

      this.$posthog.identify(user.id, {
        email: user.email,
        username: user.username,
        displayName: user.displayName,
        avatar: `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}`,
      })

      if (this.$store.state.selection.community) {
        this.$posthog.group('community', this.$store.state.selection.community.id, {
          name: this.$store.state.selection.community.name,
        })
        this.$posthog.capture('community_selected', {
          communityId: this.$store.state.selection.community.id,
          communityName: this.$store.state.selection.community.name,
        })
      }
      if (this.$store.state.selection.service) {
        this.$posthog.group('service', this.$store.state.selection.service.id, {
          name: this.$store.state.selection.service.name,
          communityId: this.$store.state.selection.community.id,
          communityName: this.$store.state.selection.community.name,
        })
        this.$posthog.capture('service_selected', {
          serviceId: this.$store.state.selection.service.id,
          serviceName: this.$store.state.selection.service.name,
          serviceCommunityId: this.$store.state.selection.community.id,
          serviceCommunityName: this.$store.state.selection.community.name,
        })
      }
    }
  },
  methods: {
    joinRoomOfService() {
      // Join a the room associated with the current service
      if (this.$store.state.selection.service) {
        this.$socket.client.emit('joinServiceRoom', this.$store.state.selection.service.id)
        setTimeout(() => {
          this.$store.dispatch('connections/CHANGE_PAGE', { pageName: this.$route.name })

          this.$store.dispatch('connections/GET_ACTIVE_CONNECTIONS')
        }, 1000)
      }
    },
  },
}
</script>
